import { useTheme } from '@mui/material/styles';
import { useMemo } from 'react';
import { Cell, Legend, Pie, PieChart, Tooltip } from 'recharts';

import { formatReportPercent } from 'shared/lib/reportFormatters';

import { AnimatedBumpSector } from './AnimatedBumpSector';
import { ReportWidgetChartTooltip } from './ReportWidgetChartTooltip';

import {
    formatPieLegendLabel,
    getAggregatedOtherLabel,
    resolvePieChartLayout,
    resolvePieTooltipRowsBuilder,
} from '../../model/lib/pieChartHelpers';
import {
    type ReportWidgetChartPoint,
    type ReportWidgetItem,
    type ReportWidgetTooltipRowsBuilder,
    getChartItemId,
} from '../../model/types';

const DEFAULT_MIN_ANGLE = 4;
const SLICE_GAP_STROKE_WIDTH = 2.5;

interface ReportWidgetPieChartProps {
    data: ReportWidgetChartPoint[];
    showLegend: boolean;
    items: ReportWidgetItem[];
    total: number;
    variant: 'pie' | 'donut';
    tooltipFormatter: (value: number) => string;
    tooltipRowsBuilder?: ReportWidgetTooltipRowsBuilder;
    width?: number;
    height?: number;
    activeItemId?: string;
    disableAnimation?: boolean;
    minAngle?: number;
    isPrinting?: boolean;
    modelLabel?: string;
    onItemClick?: (item: ReportWidgetItem) => void;
    onItemHover?: (item: ReportWidgetItem | null) => void;
}

interface PieLegendPayloadEntry {
    value?: string | number;
    color?: string;
    payload?: {
        id?: string;
        value?: number;
    };
}

const ReportWidgetPieChartLegend = ({
    payload,
    items,
    total,
    modelLabel,
}: {
    payload?: PieLegendPayloadEntry[];
    items: ReportWidgetItem[];
    total: number;
    modelLabel?: string;
}) => {
    if (!payload) {
        return null;
    }

    return (
        <ul
            style={{
                listStyle: 'none',
                margin: 0,
                padding: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                fontSize: '1.3rem',
                fontWeight: 400,
                lineHeight: '2rem',
                color: '#000000',
            }}
        >
            {payload.map((entry) => {
                const itemId = entry.payload?.id;
                const matchedItem = itemId
                    ? items.find((item) => getChartItemId(item) === itemId)
                    : undefined;
                const isAggregatedOther = Boolean(
                    matchedItem?.meta?.isAggregatedOther,
                );
                const rawValue = Number(entry.payload?.value ?? 0);
                const percent =
                    total > 0
                        ? formatReportPercent((rawValue / total) * 100)
                        : null;
                const labelText = String(entry.value ?? '');
                const displayLabel = isAggregatedOther
                    ? getAggregatedOtherLabel({
                          modelLabel,
                          meta: matchedItem?.meta,
                      })
                    : labelText;

                return (
                    <li
                        key={labelText}
                        style={{ display: 'flex', alignItems: 'center' }}
                    >
                        <span
                            style={{
                                width: '10px',
                                height: '10px',
                                borderRadius: '50%',
                                backgroundColor: entry.color || '#000000',
                                marginRight: '8px',
                                flexShrink: 0,
                            }}
                        />
                        <span>
                            {formatPieLegendLabel(displayLabel, percent)}
                        </span>
                    </li>
                );
            })}
        </ul>
    );
};

export const ReportWidgetPieChart = ({
    data,
    showLegend,
    items,
    total,
    variant,
    tooltipFormatter,
    tooltipRowsBuilder,
    width,
    height,
    activeItemId,
    disableAnimation = false,
    minAngle = DEFAULT_MIN_ANGLE,
    isPrinting = false,
    modelLabel,
    onItemClick,
    onItemHover,
}: ReportWidgetPieChartProps) => {
    const pieTooltipRowsBuilder = useMemo<ReportWidgetTooltipRowsBuilder>(
        () =>
            resolvePieTooltipRowsBuilder({
                items,
                modelLabel,
                tooltipRowsBuilder,
            }),
        [items, modelLabel, tooltipRowsBuilder],
    );

    const handlePieClick = (entry: { id?: string }) => {
        if (!onItemClick || !entry?.id) return;
        const item = items.find((i) => getChartItemId(i) === entry.id);
        if (!item) return;

        if (item.meta?.isAggregatedOther) return;

        onItemClick(item);
    };

    const handlePieMouseEnter = (entry: { id?: string }) => {
        if (!onItemHover || !entry?.id) return;
        const item = items.find((i) => getChartItemId(i) === entry.id);
        onItemHover(item ?? null);
    };

    const theme = useTheme();
    const gapColor = theme.palette.background.paper;

    const activeIndex = activeItemId
        ? data.findIndex((entry) => entry.id === activeItemId)
        : -1;

    const showLegendForRender = isPrinting ? true : showLegend;

    const {
        useRightSideLegend,
        pieCx,
        pieInnerRadius,
        pieOuterRadius,
        legendLeft,
    } = resolvePieChartLayout({
        isPrinting,
        showLegend: showLegendForRender,
        width,
        variant,
    });

    return (
        <PieChart width={width} height={height}>
            <Pie
                data={data}
                dataKey="value"
                nameKey="name"
                cx={pieCx}
                cy="50%"
                innerRadius={pieInnerRadius}
                outerRadius={pieOuterRadius}
                paddingAngle={0}
                minAngle={minAngle}
                activeIndex={activeIndex >= 0 ? activeIndex : undefined}
                isAnimationActive={!disableAnimation}
                animationDuration={250}
                animationEasing="ease-out"
                activeShape={
                    <AnimatedBumpSector
                        variant={variant}
                        disableAnimation={disableAnimation}
                    />
                }
                onClick={handlePieClick}
                onMouseEnter={handlePieMouseEnter}
                onMouseLeave={() => onItemHover?.(null)}
                cursor={onItemClick ? 'pointer' : 'default'}
            >
                {data.map((entry, index) => {
                    const isActive = index === activeIndex;
                    let fillOpacity: number;
                    if (activeIndex >= 0) {
                        fillOpacity = isActive ? 1 : 0.4;
                    } else {
                        fillOpacity = 1;
                    }
                    return (
                        <Cell
                            key={entry.id}
                            fill={entry.color}
                            stroke={gapColor}
                            strokeWidth={SLICE_GAP_STROKE_WIDTH}
                            fillOpacity={fillOpacity}
                        />
                    );
                })}
            </Pie>
            <Tooltip
                offset={2}
                content={
                    <ReportWidgetChartTooltip
                        tooltipFormatter={tooltipFormatter}
                        total={total}
                        items={items}
                        tooltipRowsBuilder={pieTooltipRowsBuilder}
                    />
                }
            />
            {showLegendForRender &&
                (useRightSideLegend ? (
                    <Legend
                        align="right"
                        verticalAlign="middle"
                        layout="vertical"
                        content={
                            <ReportWidgetPieChartLegend
                                items={items}
                                total={total}
                                modelLabel={modelLabel}
                            />
                        }
                        wrapperStyle={{
                            left: legendLeft,
                            top: '50%',
                            transform: 'translateY(-50%)',
                        }}
                    />
                ) : (
                    <Legend />
                ))}
        </PieChart>
    );
};
